import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { PhaDirectoryPanel } from "@/components/site/PhaDirectoryPanel";
import { Section8Calculators } from "@/components/site/Section8Calculators";
import { HapTimelinePanel } from "@/components/site/HapTimelinePanel";
import { hapTimelineHowToJsonLd } from "@/components/site/HapTimelinePanel.seo";
import { HqsChecklistDownload } from "@/components/site/HqsChecklistDownload";
import { Section8Resources } from "@/components/site/Section8Resources";
import { Section8CaseStudy } from "@/components/site/Section8CaseStudy";
import { LandlordLawPanel } from "@/components/site/LandlordLawPanel";
import { VoucherDemandPanel } from "@/components/site/VoucherDemandPanel";
import { SisterProductLinks } from "@/components/site/SisterProductLinks";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";

import { createFileRoute, notFound } from "@tanstack/react-router";
import { stateKeywordsMeta } from "@/lib/page-keywords";
import { getProduct } from "@/data/products";
import { getProductHero } from "@/data/productHeroes";
import { getStateHero } from "@/data/state-heroes";
import { getState } from "@/data/states";
import { getMarketStats } from "@/lib/market-stats.functions";
import { getHousingMomentum } from "@/lib/housing-momentum.functions";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { getStateFmr, getFmrByZip } from "@/lib/fmr.functions";
import { publishedInvestorCities } from "@/data/investor-cities";
import { StateProductPage } from "@/components/site/StateProductPage";
import { stateProductFaqJsonLd, stateProductServiceJsonLd, stateProductMetaDescription, buildStateFaq } from "@/components/site/StateProductPage.seo";
import { productHowToJsonLd, productArticleJsonLd } from "@/components/site/ProductPage.seo";
import { buildHead } from "@/lib/seo-head";
import { stateScenarioLocation } from "@/lib/state-location";

const SLUG = "section-8-loans";
const PRODUCT = getProduct(SLUG)!;
const HERO = getProductHero(SLUG)!;

export const Route = createFileRoute("/section-8-loans/$state/")({
  loader: async ({ params }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    const [liveStats, stateFmr, housingMomentum, countyInsights] = await Promise.all([
      getMarketStats({ data: { stateSlug: params.state } }).catch(() => null),
      getStateFmr({ data: { ...stateScenarioLocation(state.abbrev) } }).catch(() => null),
      getHousingMomentum({ data: { stateAbbrev: state.abbrev, stateName: state.name } }).catch(() => null),
      getCountyInsights({ data: { stateAbbrev: state.abbrev, placeLabel: state.name } }).catch(() => null),
    ]);
    // Seed the Section 8 calculators with the real published FMR for this state:
    // the median county/metro 2-bedroom FMR is the most representative single
    // number for a statewide page (a mean is skewed by high-cost metros).
    const twoBrValues = (stateFmr?.entries ?? [])
      .map((e) => e.rents.twoBr)
      .filter((n) => Number.isFinite(n) && n > 0)
      .sort((a, b) => a - b);
    const medianTwoBr =
      twoBrValues.length > 0
        ? twoBrValues.length % 2 === 1
          ? twoBrValues[(twoBrValues.length - 1) / 2]
          : Math.round((twoBrValues[twoBrValues.length / 2 - 1] + twoBrValues[twoBrValues.length / 2]) / 2)
        : null;
    // A statewide median is dragged down by rural counties, so prefer the median
    // FMR across the state's published investor metros — that is where borrowers
    // actually buy. Fall back to the statewide county median.
    const metroZips = publishedInvestorCities
      .filter((c) => c.stateSlug === params.state && c.centroidZip)
      .slice(0, 5)
      .map((c) => ({ zip: c.centroidZip, name: c.name }));
    const metroFmrs = (
      await Promise.all(
        metroZips.map(async (m) => {
          const res = await getFmrByZip({ data: { zip: m.zip } }).catch(() => null);
          return res && res.rents.twoBr > 0 ? { ...m, twoBr: res.rents.twoBr, year: res.year } : null;
        }),
      )
    ).filter((m): m is { zip: string; name: string; twoBr: number; year: number } => m !== null);

    const median = (values: number[]) => {
      const sorted = [...values].sort((a, b) => a - b);
      if (sorted.length === 0) return null;
      return sorted.length % 2 === 1
        ? sorted[(sorted.length - 1) / 2]
        : Math.round((sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2);
    };

    const metroMedian = median(metroFmrs.map((m) => m.twoBr));
    const fmr =
      metroMedian != null
        ? {
            value: metroMedian,
            year: metroFmrs[0]?.year ?? stateFmr?.year ?? 2026,
            label: `${metroFmrs.map((m) => m.name).join(", ")} — median 2BR FMR`,
          }
        : medianTwoBr != null
          ? {
              value: medianTwoBr,
              year: stateFmr?.year ?? 2026,
              label: `${state.name} — median of ${twoBrValues.length} HUD FMR areas`,
            }
          : null;
    return { state, liveStats, fmr, housingMomentum, countyInsights };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: `${PRODUCT.shortName} loans | Simply Approved Mortgages` }, { name: "robots", content: "noindex" }] };
    const url = `/${SLUG}/${params.state}`;
    const stateHero = getStateHero(params.state) ?? HERO;
    const title = `${PRODUCT.shortName} Loans in ${state.name} | HAP-Backed Rent | Simply Approved`;
    const description = stateProductMetaDescription(PRODUCT, state);
    return buildHead({
      title,
      description,
      url,
      keywords: stateKeywordsMeta(PRODUCT.slug, state.name),
      image: stateHero.src,
      imageAlt: stateHero.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: PRODUCT.shortName, url: `/${SLUG}` },
        { name: state.name, url },
      ],
      faqs: buildStateFaq(PRODUCT, state),
      service: { name: `${PRODUCT.shortName} Loans`, state: state.name },
      extraJsonLd: [
        stateProductServiceJsonLd(PRODUCT, state, url),
        productHowToJsonLd(PRODUCT, url),
        productArticleJsonLd(PRODUCT, url),
        hapTimelineHowToJsonLd(url),
      ],
    });
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { state, liveStats, fmr, housingMomentum, countyInsights } = Route.useLoaderData();
  return (
    <StateProductPage
      product={PRODUCT}
      state={state}
      liveStats={liveStats}
      housingMomentum={housingMomentum}
      countyInsights={countyInsights}
      hideBaseTools
      topSlot={
        <div className="space-y-8">
          <Section8Calculators
            locationLabel={state.name}
            stateSlug={state.slug}
            {...(fmr
              ? {
                  defaultFmr: fmr.value,
                  defaultMarketRent: liveStats?.medianRent2BR ?? fmr.value,
                  fmrSource: { label: fmr.label, year: fmr.year },
                }
              : {})}
          />
          <LiveDscrRateSection
            strategyCards
            bare
            id="live-rates"
            eyebrow={`Live pricing · ${state.name}`}
            heading={`Live DSCR pricing for a ${state.name} Section 8 rental scenario`}
            paragraphs={[
              `These are actual pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property in ${state.name} - rate, APR, borrower points or lender credit, estimated principal and interest, and lock period from the same quote. Pricing changes intraday and is not a loan approval, commitment, or guarantee of financing.`,
            ]}
            scenarioOverrides={{ ...stateScenarioLocation(state.abbrev) }}
            toolLabel={`${state.name} Section 8 DSCR`}
            applyProduct="section-8"
            links={[
              { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
              { to: "/rates", label: "See today's investor rate bands" },
            ]}
          />
        </div>
      }
      midSlot={
        <div className="space-y-8">
          <VoucherDemandPanel stateAbbrev={state.abbrev} stateName={state.name} />


          <PhaDirectoryPanel stateSlug={state.slug} stateName={state.name} />
          <HapTimelinePanel locationLabel={state.name} />
          <Section8CaseStudy location={state.name} />
          <HqsChecklistDownload locationLabel={state.name} />
          <LandlordLawPanel stateSlug={state.slug} stateName={state.name} />
          <SisterProductLinks self="section-8" stateSlug={state.slug} stateName={state.name} />
          <ProductReportCtaBanner
            productKey="section-8"
            variant="full"
            locationLabel={state.name}
          />
          <Section8Resources stateSlug={state.slug} stateName={state.name} />
        </div>
      }
    />
  );
}
